import {
  AdminInputPlaceholder,
  Button,
  dateTimeInitial,
  Div,
  ErrorToast,
  lightTheme,
  MaxRange,
} from '@vgrubs/components';
import { ModalPortal } from 'components/src/components/modal/modalPortal';
import 'flatpickr/dist/flatpickr.css';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  AdminDatePickerStyled,
  FlatpickrStyled,
} from './AdminDatePickerStyled';
import { InputArrowIcon } from '../../assets/svg';

function getRangeLimits(start: Date, maxRange: MaxRange) {
  const minDate = new Date(start);
  const maxDate = new Date(start);

  if (maxRange === 'month') {
    if (start.getDate() === 1) {
      // ✅ min date is start.getDate() - 30 days
      minDate.setDate(start.getDate() - 29);
      maxDate.setMonth(maxDate.getMonth() + 1);
      maxDate.setDate(0); // last day of same month
    } else {
      // ✅ 30-day sliding window
      minDate.setDate(minDate.getDate() - 30);
      maxDate.setDate(maxDate.getDate() + 30);

      // shift by -1 day
      minDate.setDate(minDate.getDate() + 1);
      maxDate.setDate(maxDate.getDate() - 1);
    }
  }

  if (maxRange === 'year') {
    minDate.setFullYear(minDate.getFullYear() - 1);
    maxDate.setFullYear(maxDate.getFullYear() + 1);

    // shift by -1 day
    minDate.setDate(minDate.getDate() + 1);
    maxDate.setDate(maxDate.getDate() - 1);
  }

  return { minDate, maxDate };
}

export const AdminDatePickerInputModal = ({
  centerFixed,
  format = 'MMM dd, yyyy',
  placeholder = 'MM/DD/YYYY',
  enableTime = true,
  mode = 'single',
  label = '',
  maxRange,
  $shadowDesign,
  component,
  externalModalState = false,
  setExternalModalState,
  onSubmit,
  beforeApplyValidation,
  $clearable = false,
  $textCenter,
  ...props
}: {
  centerFixed?: boolean;
  format?: string;
  placeholder?: string;
  value?: Date[];
  onChange?: Dispatch<SetStateAction<Date[]>> | ((date: Date[]) => void);
  options?: any;
  label?: string;
  $textCenter?: boolean;
  enableTime?: boolean;
  mode?: 'single' | 'range';
  maxRange?: MaxRange;
  $shadowDesign?: boolean;
  component?: React.ReactNode;
  externalModalState?: boolean;
  setExternalModalState?: Dispatch<SetStateAction<boolean | any>>;
  onSubmit?: () => void;
  beforeApplyValidation?: (inputValue: Date[]) => string | null;
  $clearable?: boolean;
}) => {
  const [inputValue, setInputValue] = useState(props.value || []);
  const [modal, setModal] = useState(false);
  const flatpickrRef = useRef<any>(null);

  useEffect(() => {
    setInputValue(
      props?.value?.length && props?.value?.length > 0
        ? props.value
        : [dateTimeInitial]
    );
  }, [modal]);

  const handleApply = () => {
    const error = beforeApplyValidation?.(inputValue);
    if (error) {
      return ErrorToast(error);
    }

    const fp = flatpickrRef.current?.flatpickr;

    // If user is typing in time input, force blur to trigger Flatpickr update
    const activeEl = document.activeElement as HTMLInputElement;
    if (
      activeEl &&
      activeEl.tagName === 'INPUT' &&
      fp?.calendarContainer.contains(activeEl)
    ) {
      activeEl.blur();
    }

    // Re-parse the input to ensure it's applied
    if (fp) {
      fp.setDate(fp.input.value, true); // true = triggerChange

      let latestDate = fp.selectedDates;

      if (mode === 'range' && latestDate.length === 1) {
        // Automatically duplicate the single date to form a valid range
        latestDate = [latestDate[0], latestDate[0]];
      }

      setInputValue(latestDate); // update internal state
      props.onChange?.(latestDate); // fire external change
    }

    setModal(false);
    setExternalModalState?.(false);
    onSubmit?.();
  };

  const inputValueFormatted =
    mode === 'single'
      ? DateTime.fromJSDate(props?.value?.[0] || new Date()).toFormat(format)
      : DateTime.fromJSDate(props?.value?.[0] || new Date()).toFormat(format) +
        ' - ' +
        DateTime.fromJSDate(props?.value?.[1] || new Date()).toFormat(format);

  const handleClear = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setInputValue([]);
    props.onChange?.([]);
    setModal(false);
    setExternalModalState?.(false);
  };

  return (
    <AnimatePresence>
      <Div $relative $widthSM="100%" $width="100%">
        <AdminInputPlaceholder
          label={label}
          $textCenter={$textCenter}
          onClick={() => setModal(true)}
          {...props}
          value={props?.value?.[0] ? inputValueFormatted : null}
          placeholder={placeholder}
          $shadowDesign={$shadowDesign}
          Icn={InputArrowIcon}
          onClickClear={$clearable ? handleClear : undefined}
          aria-haspopup="dialog"
          aria-expanded={modal}
        />

        <ModalPortal
          isOpen={modal || externalModalState}
          closeModal={() => {
            setModal(false);
            setExternalModalState?.(false);
          }}
          width={props.options?.noCalendar ? '240px' : '340px'}
          backgroundColor={lightTheme.white}
          padding="16px"
          paddingSM="12px"
          exitButton={false}
          zIndex={151}
        >
          <FlatpickrStyled $noCalendar={props.options?.noCalendar}>
            <AdminDatePickerStyled
              {...props}
              value={inputValue}
              onChange={(dates) => setInputValue(dates)}
              options={{
                mode,
                inline: true,
                enableTime,
                dateFormat: 'Y-m-d H:i',
                onChange: (selectedDates, dateStr, instance) => {
                  if (
                    mode === 'range' &&
                    selectedDates.length === 1 &&
                    maxRange
                  ) {
                    const { minDate, maxDate } = getRangeLimits(
                      selectedDates[0],
                      maxRange
                    );
                    instance.set('minDate', minDate);
                    instance.set('maxDate', maxDate);
                  }

                  props.options?.onChange?.(selectedDates, dateStr, instance);
                },
                ...props.options,
              }}
              ref={flatpickrRef}
              className="form-control"
            />
          </FlatpickrStyled>

          {component}

          <Button
            $selected
            $w="100%"
            $mt="16"
            $wSM="100%"
            onClick={handleApply}
            $small
          >
            Apply
          </Button>
        </ModalPortal>
      </Div>
    </AnimatePresence>
  );
};
